import React, { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Table,
    Card,
    Space,
    Tag,
    Button,
    message
} from 'antd';
import {
    PrinterOutlined,
    CodeSandboxOutlined,
    ReloadOutlined
} from "@ant-design/icons";

import wbOrdersStore from '../../stores/WBOrdersStore';

// Добавляем стили
import './OzoneOrders.css';


const WBOrders = observer(() => {
    const { orders, loading } = wbOrdersStore;
    const [refreshing, setRefreshing] = useState(false);
    const [processingOrders, setProcessingOrders] = useState(new Set());

    const fetchData = useCallback(async () => {
        setRefreshing(true);
        await wbOrdersStore.fetchOrders();
        setRefreshing(false);
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Функции для обработки действий
    const handlePrint = async (postingNumber) => {
        try {
            message.loading({ content: 'Загрузка этикетки...', key: 'printing' });
            await wbOrdersStore.printPostingLabel(postingNumber);
            message.success({ content: 'Этикетка успешно загружена', key: 'printing' });
        } catch (error) {
            message.error({ 
                content: error.response?.data?.message || error.message || 'Ошибка при загрузке этикетки', 
                key: 'printing' 
            });
        }
    };

    const handleRePrint = async (postingNumber) => {
        try {
            message.loading({ content: 'Загрузка этикетки...', key: 'reprinting' });
            await wbOrdersStore.printPostingLabel(postingNumber);
            message.success({ content: 'Этикетка успешно загружена', key: 'reprinting' });
        } catch (error) {
            message.error({
                content: error.response?.data?.message || error.message || 'Ошибка при загрузке этикетки',
                key: 'reprinting'
            });
        }
    };

    const handleProcess = async (postingNumber) => {
        setProcessingOrders(prev => new Set([...prev, postingNumber]));
        try {
            // Здесь будет вызов API для обработки заказа WB
            // await wbOrdersStore.processOrder(postingNumber);
            message.success('Заказ успешно обработан');
        } catch (error) {
            message.error(error.response?.data?.message || error.message || 'Ошибка при обработке заказа');
        } finally {
            setProcessingOrders(prev => {
                const newSet = new Set(prev);
                newSet.delete(postingNumber);
                return newSet;
            });
        }
    };

    // Функция для получения статуса заказа (заглушка)
    const getOrderStatus = (postingNumber) => {
        // Здесь должна быть логика получения статуса из store или API
        return {
            isPrinted: false, // Заглушка
            isProcessed: false // Заглушка
        };
    };

    // Функция для получения иконок статуса
    const getStatusIcons = (statuses) => {
        return (
            <Space>
                <Tag icon={<PrinterOutlined />} type="primary" color={statuses.isPrinted ? "success" : "default"}>
                </Tag>
                <Tag icon={<CodeSandboxOutlined />} type="primary" color={statuses.isProcessed ? "success" : "default"}>
                </Tag>
            </Space>
        );
    };

    const columns = [
        {
            title: 'Номер заказа',
            dataIndex: 'posting_number',
            key: 'posting_number',
            width: 150,
            fixed: 'left',
            render: (text) => (
                <div style={{ textAlign: 'center', fontWeight: 'bold' }}>
                    {text}
                </div>
            )
        },
        {
            title: 'Дата обработки',
            dataIndex: 'proc_date_str',
            key: 'proc_date_str',
            width: 120,
            align: 'center',
            render: (date) => (
                <div style={{ textAlign: 'center' }}>
                    {date}
                </div>
            )
        },
        {
            title: 'Время обработки',
            dataIndex: 'proc_time_str',
            key: 'proc_time_str',
            width: 120,
            align: 'center',
            render: (time) => (
                <div style={{ textAlign: 'center' }}>
                    {time}
                </div>
            )
        },
        {
            title: 'Дата отгрузки',
            dataIndex: 'ship_date_str',
            key: 'ship_date_str',
            width: 120,
            align: 'center',
            render: (date) => (
                <div style={{ textAlign: 'center' }}>
                    {date}
                </div>
            )
        },
        {
            title: 'Время отгрузки',
            dataIndex: 'ship_time_str',
            key: 'ship_time_str',
            width: 120,
            align: 'center',
            render: (time) => (
                <div style={{ textAlign: 'center' }}>
                    {time}
                </div>
            )
        },
        {
            title: 'День недели',
            dataIndex: 'ship_weekday',
            key: 'ship_weekday',
            width: 100,
            align: 'center',
            render: (weekday) => (
                <Tag color="blue" style={{ width: '100%', textAlign: 'center' }}>
                    {weekday}
                </Tag>
            )
        },
        {
            title: 'Статус',
            dataIndex: 'status_rus',
            key: 'status_rus',
            width: 120,
            render: (status, record) => (
                <Tag color={record.status_color} style={{ width: '100%', textAlign: 'center' }}>
                    {status}
                </Tag>
            ),
        },
        {
            title: 'Подстатус',
            dataIndex: 'substatus_rus',
            key: 'substatus_rus',
            width: 120,
            render: (substatus, record) => (
                <Tag color={record.status_color} style={{ width: '100%', textAlign: 'center' }}>
                    {substatus}
                </Tag>
            ),
        },
        {
            title: 'Склад',
            dataIndex: 'Sklad',
            key: 'Sklad',
            width: 80,
            align: 'center',
            render: (sklad) => (
                <Tag color="green" style={{ width: '100%', textAlign: 'center' }}>
                    {sklad}
                </Tag>
            )
        },
        {
            title: 'Канал',
            dataIndex: 'chanel',
            key: 'chanel',
            width: 80,
            align: 'center',
            render: (chanel) => (
                <Tag color="orange" style={{ width: '100%', textAlign: 'center' }}>
                    {chanel}
                </Tag>
            )
        },
        {
            title: 'Товары',
            dataIndex: 'products',
            key: 'products',
            width: 300,
            render: (products) => (
                <Space direction="vertical" size={4}>
                    {products?.map((product, index) => (
                        <div key={index} className="mb-2 p-2 border rounded" style={{backgroundColor: product.color + '20'}}>
                            <div className="font-semibold">{product.name}</div>
                            <div className="text-sm text-gray-600">ID: {product.offer_id}</div>
                            <div className="text-sm">Количество: {product.quantity}</div>
                            <div className="text-sm">Цвет: {product.rusColor}</div>
                        </div>
                    ))}
                </Space>
            ),
        },
        {
            title: 'Действия',
            key: 'actions',
            width: 150,
            align: 'center',
            render: (_, record) => {
                // Показываем кнопки в зависимости от статуса заказа
                if (record.status_rus === 'В работе' || record.substatus_rus === 'В работе') {
                    return (
                        <Space>
                            <Button
                                type="default"
                                onClick={() => handlePrint(record.posting_number)}
                                icon={<PrinterOutlined />}
                                size="small"
                            >
                            </Button>
                            <Button
                                type="default"
                                onClick={() => handleRePrint(record.posting_number)}
                                icon={<ReloadOutlined />}
                                size="small"
                            >
                            </Button>
                        </Space>
                    );
                }
                return null;
            }
        },
        {
            title: 'Статус обработки',
            dataIndex: 'posting_number',
            key: 'processing_status',
            width: 200,
            render: (postingNumber) => {
                const statuses = getOrderStatus(postingNumber);
                return getStatusIcons(statuses);
            }
        }
    ];


    return (
        <div className="p-4">
            <Card>
                <h2 className="text-xl font-bold mb-4">Заказы WB</h2>
                <Button onClick={fetchData} loading={refreshing} style={{ marginBottom: 16 }}>Обновить11</Button>
                <Table
                    columns={columns}
                    dataSource={orders}
                    rowKey="posting_number"
                    loading={loading}
                    scroll={{ x: 1800 }}
                    pagination={{ pageSize: 20 }}
                    size="small"
                />
            </Card>
        </div>
    );
});

export default WBOrders;